<template>
  <div ref="contentOrgEl" class="content-organization">
    <!-- plus free access callout -->
    <plus-free-access-callout
      v-if="userAuthStore.plusFreeAccess.enabled"
      tour-id="plusFreeAccessSurpriseMe"
    />

    <search-header
      :title="pageTitle"
      :has-tutorial="false"
      active="collections"
      active-action="link"
      :filter-url="filterUrl"
      :browse-url="browseUrl"
      :show-tags="false"
      :show-filter-collections="false"
    />

    <div ref="contentResultsEl" class="content-type-options">
      <div class="content-results">
        <!-- error state, modal will also show -->
        <callout v-if="errorMsg" type="error">
          <div>{{ errorMsg }}</div>
        </callout>

        <!-- result top bar -->
        <div class="results-details">
          <search-title
            :link-url="
              userAuthStore.features.FILTER_COLLECTIONS && filterCollectionData
                ? filterCollectionData.filterUrl
                : ''
            "
            :title="filterCollectionData ? filterCollectionData.title : ''"
          />
          <search-result-actions :accessibility-expander-id="accessibilityExpanderId">
            <!-- filter collection specific actions -->
            <div v-if="isOwner && filterCollectionData" class="action">
              <filter-collection-delete-button
                :filter-collection-id="filterCollectionData.id"
                @delete-cancel="handleDeleteCancel"
                @delete-confirm="handleDeleteConfirm"
                @deleted="handleFilterCollectionDeleted"
              />
            </div>
            <div v-if="isOwner && filterCollectionData" class="action">
              <filter-collection-form-button
                :entity-type="filterCollectionData.type"
                :filter-collection="filterCollectionData"
                :disabled="isDeleteConfirm"
                @saved="handleFilterCollectionUpdated"
              />
            </div>
            <div v-if="canCopy && filterCollectionData" class="action">
              <filter-collection-copy-button
                :filter-collection="filterCollectionData"
                :disabled="isDeleteConfirm"
              />
            </div>
            <div v-if="(isOwner || canShare) && filterCollectionData" class="action">
              <filter-collection-share-button
                :filter-collection="filterCollectionData"
                :disabled="isDeleteConfirm || filterCollectionData.privacy === 'private'"
              />
            </div>
          </search-result-actions>
        </div>

        <!-- results -->
        <search-result-cards
          v-if="!errorMsg && (searchResults.loading || searchResults.total > 0)"
          v-slot="{ cardOrder }"
          :loading="searchResults.loading"
          :per-page-total="searchResults.perPage"
          :current-page-total="searchResultsData.length"
          entity-type="plan"
        >
          <static-cta-card
            v-if="showStaticCard"
            url="/membership"
            :classes="staticCardContent.classes"
            title-classes="-standard"
            :large-title="staticCardContent.largeTitle"
            :title="staticCardContent.title"
            :blurb="staticCardContent.blurb"
            :style="{ order: cardOrder(staticCardIndex) }"
          />

          <video-card
            v-for="(video, index) in searchResultsData"
            :key="video.id"
            :video="video"
            :plus-pass="searchStore.plusPass"
            :cheapest-monthly-price="searchStore.cheapestMonthlyPrice"
            :style="{ order: cardOrder(adjustCardIndex(index)) }"
          />
        </search-result-cards>
        <search-no-results v-else @clear-all="clearAll" />

        <!-- pagination -->
        <pagination
          :current-page="searchResults.currentPage"
          :total-pages="searchResults.totalPages"
          :total-items="searchResults.total"
          :per-page="searchResults.perPage"
          @page-update="pageUpdate"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, ref, shallowRef, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Pagination from '../search/pagination.vue'
import SearchHeader from '@/components/search/search-header.vue'
import SearchResultActions from '@/components/search/search-result-actions.vue'
import { useUserAuthStore } from '@/stores/user-auth'
import { useSearchStore } from '@/stores/search'
import type { FilterCollection, VideoCardType } from '@/types/model-types'
import { removeArraySyntax } from '@/utils/helpers'
import { fbApi, isAbortError, objToSearchParams } from '@/utils/http-api'
import type { PlusPass, VideoFacets, VideoPagination } from '@/types/search-types'
import Callout from '@/components/notifications/callout.vue'
import SearchResultCards from '@/components/search/search-result-cards.vue'
import VideoCard from '@/components/videos/video-card.vue'
import SearchNoResults from '@/components/search/search-no-results.vue'
import { useSearchRoute } from '@/composables/search-route'
import PlusFreeAccessCallout from '@/components/notifications/plus-free-access-callout.vue'
import { updateMetaPageTitle } from '@/utils/head'
import SearchTitle from '@/components/search/search-title.vue'
import StaticCtaCard from '@/components/cards/static-cta-card.vue'
import FilterCollectionShareButton from '@/components/buttons/filter-collections/filter-collection-share-button.vue'
import FilterCollectionDeleteButton from '@/components/buttons/filter-collections/filter-collection-delete-button.vue'
import FilterCollectionFormButton from '@/components/buttons/filter-collections/filter-collection-form-button.vue'
import FilterCollectionCopyButton from '@/components/buttons/filter-collections/filter-collection-copy-button.vue'

/** STATE **/
const route = useRoute()
const router = useRouter()
const userAuthStore = useUserAuthStore()
const searchStore = useSearchStore()
const { contentResultsEl, contentOrgEl, clearAll, pageUpdate } = useSearchRoute()

const page = ref<{
  loading: boolean
  error: string
}>({
  loading: false,
  error: '',
})

const searchResults = ref<{
  loading: boolean
  error: string
  currentPage: number
  totalPages: number
  total: number
  perPage: number
}>({
  loading: false,
  error: '',
  currentPage: 0,
  totalPages: 0,
  perPage: 20,
  total: 0,
})

const filterCollectionData = shallowRef<FilterCollection | null>(null)
const searchResultsData = shallowRef<VideoCardType[]>([])
const isDeleteConfirm = ref(false)

/** NON REACTIVE **/
let searchInitAbort: AbortController | null = null
let fetchAbort: AbortController | null = null
const pageTitle = 'Browse Workout Videos'
const filterUrl = '/videos'
const browseUrl = '/videos/browse'
const staticCardIndex = 0
const accessibilityExpanderId = 'results-expander-control'

/** COMPUTED **/
const errorMsg = computed(() => page.value.error || searchResults.value.error)

// restrict edit / delete to owner
const isOwner = computed(() => {
  if (
    !userAuthStore.user.isLoggedIn ||
    !filterCollectionData.value ||
    !userAuthStore.features.FILTER_COLLECTIONS
  ) {
    return false
  }

  return filterCollectionData.value.userId === userAuthStore.user.id
})

// restrict copy to logged in and public collections
const canCopy = computed(() => {
  if (
    !userAuthStore.user.isLoggedIn ||
    !filterCollectionData.value ||
    !userAuthStore.features.FILTER_COLLECTIONS
  ) {
    return false
  }

  // fb collection, no copy for now
  if (filterCollectionData.value.userId === 0) {
    return false
  }

  // if private, must own
  if (filterCollectionData.value.privacy === 'private') {
    return isOwner.value
  }

  // if public, anyone can copy
  if (filterCollectionData.value.privacy === 'public') {
    return true
  }

  // default no copying
  return false
})

const canShare = computed(() => {
  if (!filterCollectionData.value) {
    return false
  }

  // fb collection, no copy for now
  if (filterCollectionData.value.userId === 0) {
    return false
  }

  // if public and has share code
  if (filterCollectionData.value.privacy === 'public') {
    return true
  }

  // default no sharing
  return false
})

/**
 * Show static card to be shown on page 1 for free / anon users.
 */
const showStaticCard = computed(() => {
  if (searchResults.value.currentPage !== 1) {
    return false
  }

  return !userAuthStore.features.FILTER_COLLECTIONS
})

const staticCardContent = computed(() => {
  // check query params for private filters and change content
  if (filterCollectionData.value) {
    const hasPrivateFilters = Object.keys(filterCollectionData.value.queryParams).some((key) => {
      // if key starts with "x" it is an exclusive negation filter
      if (key.startsWith('x')) {
        return true
      }

      // exclusive "workout includes" filter
      if (key === 'includes') {
        return true
      }

      return false
    })

    console.warn(hasPrivateFilters, filterCollectionData.value.queryParams)

    if (hasPrivateFilters) {
      return {
        url: '/membership',
        classes: '-opt-01',
        titleClasses: '-standard',
        largeTitle: '',
        title: 'Plus Collection',
        blurb:
          'This collection was made with FB Plus. Upgrade with a Pass or Membership to unlock all the capabilities.',
      }
    }
  }

  return {
    url: '/membership',
    classes: '-opt-02',
    titleClasses: '-standard',
    largeTitle: 'Get Plus',
    title: '',
    blurb:
      'Get exclusive content and access to programs and challenges, with advanced features to help you reach and maintain your goals.',
  }
})

/** WATCHERS **/
watch(
  () => route.query,
  () => {
    // refresh data
    fetchVideos()
  },
)

/** LIFECYCLE **/
onBeforeMount(async () => {
  // clear everything
  searchStore.$reset()

  // allow search init to finish first before fetching videos,
  // it will populate team members before syncing state from url
  await searchInit()

  // searchInit doesn't re-throw errors, so only continue if it set data
  // helps avoid users quickly toggling between filter and browse views since
  // this method is async
  if (!(searchStore.init.plusPass && searchStore.init.cheapestMonthlyPrice)) {
    return
  }

  // update page title
  if (filterCollectionData.value) {
    updateMetaPageTitle({
      title: filterCollectionData.value.title,
    })
  }

  // do not wait for this to finish before moving on
  fetchVideos()
})

// cancel fetch request, especially if going to browse view as it will
// as it will use the search store
onBeforeUnmount(() => {
  if (searchInitAbort) {
    searchInitAbort.abort()
  }

  if (fetchAbort) {
    fetchAbort.abort()
  }
})

/** METHODS **/
function handleDeleteCancel() {
  isDeleteConfirm.value = false
}

function handleDeleteConfirm() {
  isDeleteConfirm.value = true
}

async function fetchVideos() {
  // clear any error
  searchResults.value.error = ''
  searchResults.value.loading = true

  // format GET query params
  let params = route.query || {}
  params = removeArraySyntax(params)

  // reset pagination on a filter change
  if (!('page' in params)) {
    searchResults.value.currentPage = 1
  }

  // merge route params (page) with filterCollection params
  if (filterCollectionData.value) {
    params = {
      ...filterCollectionData.value.queryParams,
      ...params,
    }
  }

  try {
    // cancel any previous fetch
    if (fetchAbort) {
      fetchAbort.abort()
    }

    // re-init abort controller
    fetchAbort = new AbortController()

    // shouldn't happen
    if (!filterCollectionData.value) {
      throw new Error('Missing filter collection data')
    }

    // note: backend doesn't actually use the $id currently, just a proxy
    // to video search that enables exclusive filters
    const apiUrl = `/videos/collections/${filterCollectionData.value.id}/videos`

    const res = await fbApi.get(apiUrl, {
      searchParams: objToSearchParams(params),
      signal: fetchAbort.signal,
    })

    if (!res || !res.body || !res.body.data) {
      throw new Error(`Unable to fetch workouts, please refresh the page and try again`)
    }

    const {
      paginatedVideos,
      facets,
    }: {
      paginatedVideos: VideoPagination
      facets: VideoFacets
    } = res.body.data

    searchResults.value.total = paginatedVideos.total
    searchResults.value.currentPage = paginatedVideos.current_page
    searchResults.value.totalPages = paginatedVideos.last_page
    searchResultsData.value = paginatedVideos.data

    // pinia facets
    searchStore.updateFacets(facets)
  } catch (err) {
    // skip if Aborted
    if (isAbortError(err)) {
      console.warn('fetchVideos aborted')
      return
    }

    // oops model will show
    console.error(err)
    searchResults.value.error = `Unable to fetch workouts, please refresh the page and try again`
  } finally {
    searchResults.value.loading = false
  }
}

/**
 * Load state into searchStore
 */
async function searchInit() {
  // clear any error and set both loading stats so skeleton cards show
  page.value.error = ''
  page.value.loading = true
  searchResults.value.loading = true

  try {
    searchInitAbort = new AbortController()

    const res = await fbApi.get(`/videos/collections/${route.params.id}`, {
      signal: searchInitAbort.signal,
    })

    if (!res || !res.body || !res.body.data) {
      throw new Error(`Unable to fetch collection, please refresh the page and try again`)
    }

    const {
      plusPass,
      cheapestMonthlyPrice,
      filterCollection,
    }: {
      plusPass: PlusPass
      cheapestMonthlyPrice: string
      filterCollection: FilterCollection
    } = res.body.data

    searchStore.setSearchInit({
      plusPass,
      cheapestMonthlyPrice,
    })

    filterCollectionData.value = filterCollection
  } catch (err) {
    // skip if Aborted
    if (isAbortError(err)) {
      return
    }

    // oops model will show
    console.error(err)
    page.value.error = `Unable to initialize collection, please refresh the page and try again`
  } finally {
    page.value.loading = false
  }
}

/**
 * From child, update state
 */
function handleFilterCollectionUpdated(filterCollection: FilterCollection) {
  filterCollectionData.value = filterCollection
}

/**
 * Redirect back to browse collections page, replacing the current history entry
 */
function handleFilterCollectionDeleted() {
  router.replace('/videos/browse').catch(() => {})
}

/**
 * Account for the first static card, needed to show mark units in the correct
 * flexbox order.
 */
function adjustCardIndex(cardIndex: number) {
  // no adjustments unless the static card is shown
  if (!showStaticCard.value) {
    return cardIndex
  }

  // account for static card
  return cardIndex + 1
}
</script>
